import { generatePath, useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { ExtendedTasksResponse } from 'app/console-api/tasks/tasks-extended';
import { ErrorResult } from 'app/organizations-api/types';
import { getTaskById } from 'app/console-api';
import { HttpError } from 'app/components/utils/http-error';
import { AUTH_TOKEN, customErrorMessage, statusCode } from 'app/constants';
import { SIGN_IN_PATH, APPS_TASKS_EDIT_PATH } from 'app/route/paths';
import { SvgIcon } from '@make-software/cspr-design';
import { SkeletonLoader } from 'app/components/common/skeleton-loader/skeleton-loader';
import {
  PageContainer,
  StyledCaption,
  ViewPageContainer
} from 'app/components/common/common-styles';
import { ErrorWindow } from 'app/components/common/error-window/error-window';
import NotFoundIcon from 'assets/logos/no-found.svg';
import { TaskViewPageContent } from 'app/components/management/apps/tasks/view/task-view-page-content';
import { useTabsCtx } from 'app/components/management/apps/tabs-page-layout';
import { RewardsType } from 'app/console-api/apps/types';
import { getRewardsTypes } from 'app/console-api/apps/rewards';

export const TaskViewPage = () => {
  const { appID } = useTabsCtx();
  const { id } = useParams<{ id: string }>();
  const [task, setTask] = useState<ExtendedTasksResponse | null>(null);
  const [unexpectedError, setUnexpectedError] = useState<ErrorResult | null>(null);
  const [rewardsTypes, setRewardsTypes] = useState<RewardsType[]>([]);

  const { setBreadcrumbs } = useTabsCtx();

  React.useEffect(() => {
    if (!task) {
      return;
    }
    setBreadcrumbs([
      {
        label: task.task.name
      }
    ]);
    return () => setBreadcrumbs([]);
  }, [task]);

  useEffect(() => {
    if (!id) {
      console.error('No ID provided in the route.');
      return;
    }

    getRewardsTypes(appID)
      .then((response) => setRewardsTypes(response.data || []))
      .catch((error: any) => {
        console.error('Failed to fetch rewards types', error);
      });

    getTaskById(appID, id)
      .then((response) => {
        setTask(response);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          switch (error.statusCode) {
            case statusCode.validation_error:
              setUnexpectedError({
                code: error.statusCode.toString(),
                message: error.message
              });
              break;
            case statusCode.authentication_error:
              localStorage.removeItem(AUTH_TOKEN);
              window.location.href = SIGN_IN_PATH;
              return;
            case statusCode.unexpected_error:
              setUnexpectedError(customErrorMessage);
              break;
            default:
              setUnexpectedError(customErrorMessage);
              break;
          }
        } else {
          setUnexpectedError(customErrorMessage);
        }
      });
  }, []);

  const navigate = useNavigate();
  const onEdit = () => {
    navigate(generatePath(APPS_TASKS_EDIT_PATH, { appID: appID, id: id! }));
  };

  if (!task) {
    return (
      <PageContainer>
        <ViewPageContainer>
          <SkeletonLoader height={'300px'} />
        </ViewPageContainer>
      </PageContainer>
    );
  }

  if (unexpectedError) {
    return (
      <ErrorWindow
        withModal
        title={unexpectedError.code}
        information={unexpectedError.message}
        icon={<SvgIcon src={NotFoundIcon} width={219} height={125} />}
      />
    );
  }

  return (
    <PageContainer>
      <ViewPageContainer>
        <StyledCaption />
        <TaskViewPageContent rewardsTypes={rewardsTypes} data={task} onEdit={onEdit} />
      </ViewPageContainer>
    </PageContainer>
  );
};
